import type { EstadoPrueba, Gesto, IntentoDeGesto } from '@validate/core';

/**
 * El cliente de la API para el flujo de verificación.
 *
 * ── LA REGLA, QUE YA SE ROMPIÓ UNA VEZ Y SALIÓ CARA ─────────────────────────────
 *
 * **Antes de escribir un tipo de respuesta: MIRAR lo que el endpoint devuelve de verdad** —
 * llamándolo, o leyendo el código del servidor que construye la respuesta.
 *
 * En el panel se inventó la forma de `/auth/me` de memoria. El typecheck no lo salvó —porque
 * el tipo era la mentira— y la pantalla salió en blanco con `Cannot read properties of
 * undefined (reading 'split')`. Un tipo inventado es peor que no tener tipos: da confianza
 * falsa.
 */

const BASE = import.meta.env['VITE_API_URL'] ?? '/api';

/**
 * Lo que la pantalla de inicio muestra antes de que la persona toque nada.
 *
 * Cotejado el 17/07/2026 contra `PortadaPublica` en
 * `apps/api/src/modules/verificacion/sesion.service.ts`: coincide campo a campo
 * (documentoEnmascarado, paraQuien, puedeEmpezar, motivo, marca{logoUrl, acento, acento2,
 * tipografia}). Si el servidor cambia su forma, cambiar ESTA con el fichero delante.
 *
 * El documento va SIEMPRE enmascarado. Quien abre este enlace puede no ser la persona
 * correcta: a la correcta, los últimos cuatro dígitos le bastan para reconocerse; a cualquier
 * otro no le regalan un documento que no tenía.
 */
export interface Portada {
  documentoEnmascarado: string | null;
  /** El nombre del CLIENTE que pide la verificación. No el del sujeto. */
  paraQuien: string;
  puedeEmpezar: boolean;
  motivo: string;
  marca: Marca;
}

/**
 * La marca del cliente. Viaja con la SESIÓN, no con el subdominio.
 *
 * Es deliberado: el SDK va embebido en la web del cliente, y ahí no hay subdominio nuestro
 * del que leer nada. Si la marca dependiera del dominio, el SDK saldría sin marca — que es
 * justo el caso en que más falta hace.
 */
export interface Marca {
  logoUrl: string | null;
  /** Hex. El defecto es #3332F2, el de Valídate, medido del Figma. */
  acento: string | null;
  acento2: string | null;
  tipografia: string | null;
}

export class SesionNoDisponible extends Error {}

/** Trae la portada de una sesión. */
export async function portada(token: string): Promise<Portada> {
  const r = await fetch(`${BASE}/verificacion/sesion/${encodeURIComponent(token)}`, {
    headers: { Accept: 'application/json' },
  });

  if (!r.ok) {
    // No se distingue 404 de 410 de 500 a propósito, y no es pereza: a quien abre el enlace,
    // "el enlace no sirve" es lo único accionable. Decirle "500" no le sirve de nada, y
    // decirle "404" le confirmaría a un curioso que el token que probó no existe.
    throw new SesionNoDisponible(
      'Este enlace no está disponible. Pídele uno nuevo a quien te lo envió.',
    );
  }

  return (await r.json()) as Portada;
}

/**
 * La prueba de vida: dos llamadas, las dos POST, las dos públicas (el token ES la
 * autorización — ver app.module.ts de la API).
 *
 * ── ESTAS FORMAS NO SON INVENTADAS ──────────────────────────────────────────────
 *
 * Están copiadas de `apps/api/src/modules/verificacion/prueba-de-vida.service.ts`
 * (`Reto`, `Resolucion`) y de su controller (`GestosDto`), leídos el 17/07/2026. Leer el
 * código que construye la respuesta cuenta como mirar el JSON; escribirla de memoria, no.
 */
export interface Reto {
  /** Los gestos, en orden. La pantalla los pide uno a uno. */
  secuencia: Gesto[];
}

export interface Resolucion {
  estado: EstadoPrueba;
  completados: number;
  /** Lo que se le dice a la PERSONA. Nunca el detalle técnico. */
  mensaje: string;
}

/**
 * Pide un reto. **GASTA un intento de tres** — está razonado en el servidor: si pedir fuera
 * gratis, un atacante pediría secuencias hasta que saliera la que coincide con su vídeo.
 *
 * Quien llama tiene que tratarlo como algo caro: una vez por pantalla de gestos, nunca en un
 * efecto que pueda dispararse dos veces.
 */
export async function pedirReto(token: string): Promise<Reto> {
  const r = await fetch(`${BASE}/verificacion/sesion/${encodeURIComponent(token)}/reto`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
  });

  if (!r.ok) {
    // Caducada, sin intentos, o el token no existe: para la persona es lo mismo — este
    // enlace ya no da más de sí, y la salida es quien se lo envió.
    throw new SesionNoDisponible(
      'Este enlace ya no está disponible. Pídele uno nuevo a quien te lo envió.',
    );
  }

  return (await r.json()) as Reto;
}

/** Envía lo que la persona hizo. El servidor compara contra lo que ÉL pidió. */
export async function enviarGestos(
  token: string,
  intentos: readonly IntentoDeGesto[],
): Promise<Resolucion> {
  const r = await fetch(`${BASE}/verificacion/sesion/${encodeURIComponent(token)}/gestos`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ intentos }),
  });

  if (!r.ok) {
    throw new SesionNoDisponible(
      'Este enlace ya no está disponible. Pídele uno nuevo a quien te lo envió.',
    );
  }

  return (await r.json()) as Resolucion;
}

/**
 * ¿Lo capturado parece de verdad el reverso de un documento? El servidor lo mira con OCR
 * real (MRZ o número) SIN completar nada. Es la RED DE SEGURIDAD de la comprobación en
 * vivo (la banda del MRZ se detecta por píxeles antes de disparar, en `deteccion.ts`).
 *
 * Con timeout de 25 s: el OCR multi-pasada puede atascarse, y una persona esperando a un
 * spinner no distingue "lento" de "roto". Si expira, el `catch` de la pantalla lo deja
 * pasar — el veredicto final analiza todo de nuevo y tiene la última palabra.
 */
export interface ReversoComprobado {
  legible: boolean;
}

export async function comprobarReverso(
  token: string,
  reverso: string,
): Promise<ReversoComprobado> {
  const r = await fetch(`${BASE}/verificacion/sesion/${encodeURIComponent(token)}/reverso`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ reverso }),
    signal: AbortSignal.timeout(25_000),
  });

  if (!r.ok) {
    throw new SesionNoDisponible(
      'Este enlace ya no está disponible. Pídele uno nuevo a quien te lo envió.',
    );
  }

  return (await r.json()) as ReversoComprobado;
}

/**
 * #79 — la evidencia: los píxeles al servidor.
 *
 * Forma de `EvidenciaController`/`EvidenciaService`, escritos en el mismo cambio que esto:
 * no hay forma que adivinar. El servidor coteja el rostro contra el documento, mide pulso
 * (rPPG) y ruido de sensor (PRNU), crea la Verification y devuelve UNA de tres palabras.
 *
 * Es la llamada LENTA del flujo (OCR + detección por fotograma en CPU): quien la haga debe
 * tener la pantalla de VERIFICANDO delante. Y es segura de reintentar: una sesión con
 * veredicto no se re-evalúa — el reintento devuelve el desenlace que ya existe.
 */
export interface EvidenciaDelCliente {
  anverso: string;
  reverso: string;
  rostro: string;
  /** Ráfaga cronológica del rostro, para /pulse y /prnu. */
  fotogramas: readonly string[];
  fps: number;
}

export interface ResultadoDeEvidencia {
  desenlace: 'verificado' | 'no_verificado' | 'pendiente';
  mensaje: string;
}

export async function enviarEvidencia(
  token: string,
  evidencia: EvidenciaDelCliente,
): Promise<ResultadoDeEvidencia> {
  const r = await fetch(`${BASE}/verificacion/sesion/${encodeURIComponent(token)}/evidencia`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(evidencia),
    // Sin AbortSignal.timeout a propósito: la evaluación puede tardar más de un minuto y el
    // navegador no debe rendirse antes que el servidor. El servidor ya tiene sus timeouts.
  });

  if (!r.ok) {
    throw new SesionNoDisponible(
      'Este enlace ya no está disponible. Pídele uno nuevo a quien te lo envió.',
    );
  }

  return (await r.json()) as ResultadoDeEvidencia;
}
